import { c as _c } from "react/compiler-runtime";
import type { ChainId, MarketParams } from '@morpho-org/blue-sdk';
import type { ActionRequirement } from '@morpho-org/morpho-sdk';
import type { Address } from 'viem';
import { isDefined } from '@morpho-org/morpho-ts';
import { TEMPO_CONNECTOR_ID } from '@repo/web3/utils';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { resolveSignatureSupport } from '@/contexts/Web3Context/utils/resolveSignatureSupport';
import { useWeb3Context } from '@/contexts/Web3Context/Web3.context';
import { useVaultV2 } from '../../../hooks/useVaultV2';
import { useVaultV2Position } from '../../../hooks/useVaultV2Position';
import { capInKindExitToSharesWithFee } from './deallocation';
import { inKindRedemptionFeeAssets } from './inKindRedemption.helpers';
export function useVaultV2InKindRedeemTransaction(t0) {
  const $ = _c(64);
  const {
    vaultAddress,
    chainId,
    exitAssets,
    marketParamsList
  } = t0;
  const {
    account,
    viemClient,
    allowSignatureRequirements
  } = useWeb3Context();
  const {
    vault,
    vaultData,
    vaultDataBlockNumber,
    isVaultDataPlaceholder,
    isLoading: isVaultLoading,
    error: vaultError
  } = useVaultV2(vaultAddress);
  let t1;
  if ($[0] !== chainId || $[1] !== vaultAddress) {
    t1 = {
      vaultAddress,
      chainId
    };
    $[0] = chainId;
    $[1] = vaultAddress;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    userVaultShares,
    isUserVaultSharesPlaceholder,
    isLoading: isPositionLoading,
    error: positionError
  } = useVaultV2Position(t1);
  let t2;
  if ($[3] !== exitAssets || $[4] !== userVaultShares || $[5] !== vaultData) {
    t2 = isDefined(vaultData) && isDefined(userVaultShares) ? capInKindExitToSharesWithFee({
      exitAssets,
      userVaultShares,
      vault: vaultData,
      feeOf: assets => inKindRedemptionFeeAssets(vaultData, assets)
    }) : exitAssets;
    $[3] = exitAssets;
    $[4] = userVaultShares;
    $[5] = vaultData;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const safeExitAssets = t2;
  const t3 = account.connector?.id === TEMPO_CONNECTOR_ID;
  let t4;
  if ($[7] !== account.isByteCodeResolved || $[8] !== account.isContract || $[9] !== account.isSafe || $[10] !== allowSignatureRequirements || $[11] !== t3) {
    t4 = resolveSignatureSupport({
      isSafe: account.isSafe,
      isTempoWallet: t3,
      allowSignatureRequirements,
      isByteCodeResolved: account.isByteCodeResolved,
      isContract: account.isContract
    });
    $[7] = account.isByteCodeResolved;
    $[8] = account.isContract;
    $[9] = account.isSafe;
    $[10] = allowSignatureRequirements;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const supportSignature = t4;
  const t5 = account.address;
  let t6;
  if ($[13] !== safeExitAssets) {
    t6 = safeExitAssets.toString();
    $[13] = safeExitAssets;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== marketParamsList) {
    t7 = marketParamsList.map(_temp);
    $[15] = marketParamsList;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== account.address || $[18] !== supportSignature || $[19] !== t6 || $[20] !== t7 || $[21] !== vaultAddress || $[22] !== vaultDataBlockNumber || $[23] !== viemClient.uid) {
    t8 = [vaultAddress, t5, t6, t7, vaultDataBlockNumber, supportSignature, viemClient.uid];
    $[17] = account.address;
    $[18] = supportSignature;
    $[19] = t6;
    $[20] = t7;
    $[21] = vaultAddress;
    $[22] = vaultDataBlockNumber;
    $[23] = viemClient.uid;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const queryKeyTail = t8;
  let t9;
  if ($[25] !== queryKeyTail) {
    t9 = ["inKindRedeem", ...queryKeyTail];
    $[25] = queryKeyTail;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== account.address || $[28] !== marketParamsList || $[29] !== safeExitAssets || $[30] !== vault || $[31] !== vaultData) {
    t10 = () => {
      if (!vaultData || !account.address) {
        return null;
      }
      return vault.inKindRedeem({
        amount: safeExitAssets,
        marketParamsList,
        vaultData,
        userAddress: account.address
      });
    };
    $[27] = account.address;
    $[28] = marketParamsList;
    $[29] = safeExitAssets;
    $[30] = vault;
    $[31] = vaultData;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== account.address || $[34] !== isUserVaultSharesPlaceholder || $[35] !== isVaultDataPlaceholder || $[36] !== marketParamsList.length || $[37] !== userVaultShares || $[38] !== vaultData) {
    t11 = !!account.address && !!vaultData && !isVaultDataPlaceholder && isDefined(userVaultShares) && !isUserVaultSharesPlaceholder && marketParamsList.length > 0;
    $[33] = account.address;
    $[34] = isUserVaultSharesPlaceholder;
    $[35] = isVaultDataPlaceholder;
    $[36] = marketParamsList.length;
    $[37] = userVaultShares;
    $[38] = vaultData;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== t10 || $[41] !== t11 || $[42] !== t9) {
    t12 = {
      queryKey: t9,
      queryFn: t10,
      enabled: t11,
      retry: false,
      gcTime: 0
    };
    $[40] = t10;
    $[41] = t11;
    $[42] = t9;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const {
    data: inKindRedeem,
    isLoading: isBuilderLoading,
    error: builderError
  } = useQuery(t12);
  let t13;
  if ($[44] !== queryKeyTail) {
    t13 = ["requirements-inKindRedeem", ...queryKeyTail];
    $[44] = queryKeyTail;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== inKindRedeem) {
    t14 = async () => (await inKindRedeem?.getRequirements()) ?? [];
    $[46] = inKindRedeem;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const t15 = !!inKindRedeem && !!account.address && !isVaultDataPlaceholder && !isUserVaultSharesPlaceholder;
  let t16;
  if ($[48] !== t13 || $[49] !== t14 || $[50] !== t15) {
    t16 = {
      queryKey: t13,
      queryFn: t14,
      enabled: t15,
      gcTime: 0
    };
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  const {
    data: requirements,
    isLoading: isRequirementsLoading,
    error: requirementsError
  } = useQuery(t16);
  const t17 = inKindRedeem ?? undefined;
  const t18 = builderError ?? requirementsError;
  let t19;
  if ($[52] !== requirements) {
    t19 = isDefined(requirements);
    $[52] = requirements;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const t20 = vaultError ?? positionError ?? builderError ?? requirementsError;
  const t21 = isVaultLoading || isPositionLoading || isBuilderLoading || isRequirementsLoading;
  let t22;
  if ($[54] !== requirements || $[55] !== safeExitAssets || $[56] !== t17 || $[57] !== t18 || $[58] !== t19 || $[59] !== t20 || $[60] !== t21 || $[61] !== userVaultShares || $[62] !== vaultData) {
    t22 = {
      vaultData,
      userVaultShares,
      safeExitAssets,
      transaction: t17,
      sdkBuildError: t18,
      requirements,
      areRequirementsReady: t19,
      error: t20,
      isLoading: t21
    };
    $[54] = requirements;
    $[55] = safeExitAssets;
    $[56] = t17;
    $[57] = t18;
    $[58] = t19;
    $[59] = t20;
    $[60] = t21;
    $[61] = userVaultShares;
    $[62] = vaultData;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  return t22;
}
function _temp(market) {
  return market.id;
}